import type { EntityType } from "./api";

/**
 * WORLD-10 — the closed relation vocabulary, as the interface reads it.
 *
 * The mirror of `Factorium.Pipeline.RelationTypes`: the same eleven names, the same direction and
 * the same kinds each joins. The Server refuses anything else, so this list is what lets the
 * add-a-relation form offer only what will be accepted — a person picked on one end narrows
 * the types, and a type narrows who can be on the other end — rather than letting somebody
 * compose a refusal.
 */
export type RelationTypeName =
  | "reports_to"
  | "member_of"
  | "leads"
  | "works_at"
  | "contact_for"
  | "owns"
  | "uses"
  | "part_of"
  | "feature_of"
  | "customer_of"
  | "supplier_of";

export interface RelationTypeMeta {
  name: RelationTypeName;
  /** Read subject-first: "Jan **reports to** Renée". */
  label: string;
  /** Read from the object's page: "Renée **has as a report** Jan". */
  inverseLabel: string;
  subjectKinds: EntityType[];
  objectKinds: EntityType[];
}

// An internal product (0072) is an offering everywhere except customer_of: nobody outside buys it.
const OFFERINGS: EntityType[] = ["product", "internal_product", "service"];
const PARTS: EntityType[] = [...OFFERINGS, "team", "initiative", "location", "event"];

export const RELATION_TYPES: RelationTypeMeta[] = [
  { name: "reports_to", label: "reports to", inverseLabel: "has as a report", subjectKinds: ["person"], objectKinds: ["person"] },
  {
    name: "member_of", label: "is a member of", inverseLabel: "has as a member",
    subjectKinds: ["person", "team"], objectKinds: ["team", "initiative"],
  },
  { name: "leads", label: "leads", inverseLabel: "is led by", subjectKinds: ["person"], objectKinds: ["team", "initiative"] },
  { name: "works_at", label: "works at", inverseLabel: "employs", subjectKinds: ["person"], objectKinds: ["customer", "supplier"] },
  {
    name: "contact_for", label: "is the contact for", inverseLabel: "has as its contact",
    subjectKinds: ["person"], objectKinds: ["customer", "supplier", ...OFFERINGS, "feature", "initiative", "team"],
  },
  {
    name: "owns", label: "owns", inverseLabel: "is owned by",
    subjectKinds: ["person", "team"], objectKinds: [...OFFERINGS, "feature", "initiative", "location"],
  },
  {
    name: "uses", label: "uses", inverseLabel: "is used by",
    subjectKinds: ["person", "team", "customer", "initiative"], objectKinds: [...OFFERINGS, "feature"],
  },
  { name: "part_of", label: "is part of", inverseLabel: "includes", subjectKinds: PARTS, objectKinds: PARTS },
  {
    name: "feature_of", label: "is a feature of", inverseLabel: "has as a feature",
    subjectKinds: ["feature"], objectKinds: OFFERINGS,
  },
  {
    name: "customer_of", label: "is a customer of", inverseLabel: "has as a customer",
    subjectKinds: ["customer"], objectKinds: ["supplier", "product", "service"],
  },
  {
    name: "supplier_of", label: "supplies", inverseLabel: "is supplied by",
    subjectKinds: ["supplier"], objectKinds: [...OFFERINGS, "initiative", "team", "customer"],
  },
];

const BY_NAME = new Map(RELATION_TYPES.map((type) => [type.name, type]));

export function relationTypeMeta(name: string): RelationTypeMeta | undefined {
  return BY_NAME.get(name as RelationTypeName);
}

/** The words a relation reads in, subject-first; an unknown type (a row older than the
 * vocabulary) reads as its stored name with the underscores taken out. */
export function relationLabel(name: string): string {
  return relationTypeMeta(name)?.label ?? name.replaceAll("_", " ");
}

/**
 * One direction of one type, as the add form offers it: "this entity *reports to* …" when this
 * entity is the subject, "… *reports to* this entity" (shown as the inverse label) when it is
 * the object. Only directions this entity's kind can stand in are returned.
 */
export interface RelationChoice {
  key: string;
  type: RelationTypeMeta;
  thisIsSubject: boolean;
  /** What the menu shows, read from this entity's side. */
  label: string;
  /** Who may be on the other end. */
  otherKinds: EntityType[];
}

export function relationChoicesFor(kind: EntityType): RelationChoice[] {
  const choices: RelationChoice[] = [];

  for (const type of RELATION_TYPES) {
    if (type.subjectKinds.includes(kind)) {
      choices.push({ key: `${type.name}:s`, type, thisIsSubject: true, label: type.label, otherKinds: type.objectKinds });
    }
    if (type.objectKinds.includes(kind)) {
      choices.push({
        key: `${type.name}:o`, type, thisIsSubject: false, label: type.inverseLabel, otherKinds: type.subjectKinds,
      });
    }
  }

  return choices;
}
